// Customer A–F Grading — admin run controls + runs history.
// The daily cron ships OFF by default (owner 2026-08-26); this page is how
// grading runs happen until it's turned on: "Run all" grades every gradable
// entity, "Run sample" grades a random sample of N. Grade changes only ever
// PROPOSE — the review gate publishes them.

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  customerGradingApi,
  type GradingRunSummary,
} from '../services/customerGrading';
import { useAuth } from '../context/AuthContext';
import styles from './CustomerGradingPage.module.css';

function fmtWhen(iso: string | null): string {
  if (!iso) return '–';
  const d = new Date(iso.endsWith('Z') || iso.includes('+') ? iso : `${iso}Z`);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function scopeLabel(run: GradingRunSummary): string {
  const scope = run.requested_scope ?? '–';
  return run.sample_size ? `${scope} · sample ${run.sample_size}` : scope;
}

function transitionsLabel(t: Record<string, number> | null): string {
  if (!t || Object.keys(t).length === 0) return '–';
  return Object.entries(t)
    .map(([k, v]) => `${v} ${k}`)
    .join(', ');
}

const STATUS_CLASS: Record<string, string> = {
  completed: styles.stCompleted,
  failed: styles.stFailed,
  running: styles.stRunning,
  pending: styles.stPending,
};

export function CustomerGradingPage() {
  // Run / approve powers need grading:review (admins pass); executive readers
  // (exec:portal) see runs + the pending count read-only.
  const { hasScope } = useAuth();
  const canRun = hasScope('grading:review');
  const [runs, setRuns] = useState<GradingRunSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [sampleSize, setSampleSize] = useState('25');
  const [dispatching, setDispatching] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const timerRef = useRef<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [res, pending] = await Promise.all([
        customerGradingApi.listRuns(25, 0),
        customerGradingApi.listPending().catch(() => null),
      ]);
      setRuns(res.items);
      setTotal(res.total);
      if (pending) setPendingCount(pending.length);
    } catch {
      setError('Could not load grading runs — are you an admin?');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load + gentle auto-refresh while any run is still in flight.
  useEffect(() => {
    void load();
  }, [load]);
  useEffect(() => {
    const inFlight = runs.some(
      (r) => r.status === 'pending' || r.status === 'running',
    );
    if (!inFlight) return;
    timerRef.current = window.setInterval(() => void load(), 15000);
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    };
  }, [runs, load]);

  const dispatch = useCallback(
    async (sample: number | null) => {
      setDispatching(true);
      setNote(null);
      try {
        const res = await customerGradingApi.run(
          sample ? { scope: 'all', sample_size: sample } : { scope: 'all' },
        );
        setNote(res.detail);
        // The run row lands when the worker picks the task up.
        window.setTimeout(() => void load(), 2000);
      } catch {
        setNote('Could not queue the run — are you an admin, and is the worker up?');
      } finally {
        setDispatching(false);
      }
    },
    [load],
  );

  const runSample = useCallback(() => {
    const n = Number.parseInt(sampleSize, 10);
    if (!Number.isFinite(n) || n < 1 || n > 1000) {
      setNote('Sample size must be a whole number between 1 and 1000.');
      return;
    }
    void dispatch(n);
  }, [dispatch, sampleSize]);

  return (
    <div className={styles.page}>
      <div className={styles.headRow}>
        <div className={styles.head}>
          <h1>Customer Grading</h1>
          <p>
            A–F letter grades per customer (quality + on-time) and item
            (quality). The daily schedule is off — runs happen from here.
            Grade changes only propose; the review gate publishes them
            {pendingCount !== null && (
              <> · <strong>{pendingCount}</strong> change{pendingCount === 1 ? '' : 's'} pending review</>
            )}
            .
          </p>
        </div>
      </div>

      {canRun && (
      <div className={styles.controls}>
        <button
          type="button"
          className={styles.primaryBtn}
          disabled={dispatching}
          onClick={() => void dispatch(null)}
        >
          Run all
        </button>
        <span className={styles.sampleGroup}>
          <button
            type="button"
            className={styles.secondaryBtn}
            disabled={dispatching}
            onClick={runSample}
          >
            Run sample of
          </button>
          <input
            className={styles.sampleInput}
            type="number"
            min={1}
            max={1000}
            value={sampleSize}
            onChange={(e) => setSampleSize(e.target.value)}
            aria-label="Sample size"
          />
        </span>
        {note && <span className={styles.note}>{note}</span>}
      </div>
      )}

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Queued</th>
              <th>Status</th>
              <th>Trigger</th>
              <th>Scope</th>
              <th>Mode</th>
              <th>Entities</th>
              <th>Grades</th>
              <th>Proposals</th>
              <th>Finished</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={9} className={styles.empty}>Loading…</td></tr>
            )}
            {!loading && runs.length === 0 && (
              <tr>
                <td colSpan={9} className={styles.empty}>
                  No grading runs yet — queue one above.
                </td>
              </tr>
            )}
            {runs.map((r) => (
              <tr key={r.run_id} title={r.error ?? undefined}>
                <td>{fmtWhen(r.created_at)}</td>
                <td>
                  <span className={`${styles.status} ${STATUS_CLASS[r.status] ?? ''}`}>
                    {r.status}
                  </span>
                </td>
                <td>{r.trigger}</td>
                <td>{scopeLabel(r)}</td>
                <td>{r.mode ?? r.rubric_version}</td>
                <td>
                  {r.customers_considered ?? '–'} cust · {r.items_considered ?? '–'} items
                </td>
                <td>{r.grades_written ?? '–'}</td>
                <td>{transitionsLabel(r.state_transitions)}</td>
                <td>{fmtWhen(r.completed_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {total > runs.length && (
        <p className={styles.more}>Showing latest {runs.length} of {total} runs.</p>
      )}
    </div>
  );
}
